"use client";

import {
  type FormEvent,
  type FormHTMLAttributes,
  useCallback,
  useMemo,
  type ReactNode,
  useRef,
  useState,
  type FC,
  type Ref,
} from "react";

import { Flex, type FlexProps } from "../Flex";
import { mergeRefs } from "../../utils/mergeRefs";
import {
  FormContext,
  type FieldMetadata,
  type FormContextState,
  type FormData,
  type FormFieldSchema,
  type FormFieldsSnapshot,
  type TranslationKey,
  type ValidationErrors,
  type ValidationResult,
} from "../../contexts/FormContext";
import { useMaybePubSub } from "../../hooks/usePubSub";
import { useDebug } from "../../hooks/useDebug";
import { useFormNavigationGuard } from "../../hooks/useFormNavigationGuard";

import { formStyle } from "./Form.css";

export type FormOnSubmitFn = (
  e: FormEvent,
  valid: boolean,
  errors: ValidationErrors,
  data: FormData,
) => void;

/**
 * Internal subcomponent that registers a navigation guard while the
 * surrounding Form is dirty. Rendered as a child of the form when
 * `trackDirty` is set so it can read the FormContext.
 */
const FormNavigationGuard: FC = () => {
  useFormNavigationGuard();
  return null;
};

export type FormProps = Omit<FlexProps, "onSubmit"> &
  Omit<FormHTMLAttributes<HTMLFormElement>, "onSubmit"> & {
    /**
     * The form submit button.
     */
    submit?: ReactNode;
    /**
     * Whether the form is disabled.
     */
    disabled?: boolean;
    /**
     * When true, the form tracks whether any field value has changed
     * from its initial value and exposes `isDirty` via FormContext.
     */
    trackDirty?: boolean;
    /**
     * Called whenever a field value changes with a snapshot of all current
     * field values and their validation errors.
     */
    onValuesChange?: (data: FormFieldsSnapshot) => void;
    /**
     * Called when the field schema changes — on field registration,
     * deregistration, and value changes. Provides the full enriched
     * schema (fieldKey, label, type, step, value) for AI agent awareness.
     */
    onSchemaChange?: (schema: FormFieldSchema[]) => void;
    /**
     * The form submit handler.
     */
    onSubmit?: FormOnSubmitFn;
    /**
     * The form children.
     */
    children?: ReactNode;
    /**
     * Ref forwarded to the root element
     */
    ref?: Ref<HTMLFormElement>;
  };

/**
 * A form component that wraps form elements.
 */
export const Form: FC<FormProps> = ({
  gap = "s",
  disabled = false,
  trackDirty = false,
  onSubmit,
  onValuesChange,
  onSchemaChange,
  children,
  ref,
  ...props
}) => {
  const pubSub = useMaybePubSub();
  const { log } = useDebug();
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [customDisabled, setCustomDisabled] = useState(false);

  const formFields = useRef<
    Record<
      string,
      {
        validate: () => ValidationResult;
        name?: string;
        getValue?: () => unknown;
        metadata?: FieldMetadata;
      }
    >
  >({});
  const initialFieldValuesRef = useRef<Record<string, unknown>>({});
  const [isDirty, setIsDirty] = useState(false);
  const [schemaVersion, setSchemaVersion] = useState(0);

  const errorsRef = useRef<ValidationErrors>(errors);
  errorsRef.current = errors;

  const formDisabled = disabled || customDisabled;

  /**
   * Disables the form programmatically
   */
  const disable = useCallback(() => setCustomDisabled(true), []);

  /**
   * Enables the form programmatically
   */
  const enable = useCallback(() => setCustomDisabled(false), []);

  /**
   * Compares current field values against initial snapshots.
   */
  const checkDirty = useCallback(() => {
    const changedFields: string[] = [];
    const dirty = Object.entries(formFields.current).some(
      ([uuid, { getValue, name }]) => {
        if (!getValue) return false;
        const current = getValue();
        const initial = initialFieldValuesRef.current[uuid];
        const changed = current !== initial;
        if (changed) {
          changedFields.push(name ?? uuid);
        }
        return changed;
      },
    );
    if (dirty) {
      log("lightBlue", "Form", "checkDirty → DIRTY", changedFields);
    }
    setIsDirty(dirty);
  }, [log]);

  /**
   * Returns a snapshot of all registered field values and their
   * validation errors, keyed by name (or uuid).
   */
  const getFormData = useCallback((): FormFieldsSnapshot => {
    const data: FormFieldsSnapshot = {};
    for (const [uuid, { name, getValue }] of Object.entries(
      formFields.current,
    )) {
      if (getValue) {
        data[name ?? uuid] = {
          value: getValue(),
          errors: errorsRef.current[uuid] ?? [],
        };
      }
    }
    return data;
  }, []);

  const formRef = useRef<HTMLFormElement>(null);

  /**
   * Cached DOM-ordered field UUIDs. Invalidated on register/deregister
   * so the expensive querySelectorAll only runs on structural changes,
   * not on every keystroke.
   */
  const domOrderCacheRef = useRef<string[] | null>(null);

  const getDomOrderedUuids = useCallback((): string[] => {
    if (domOrderCacheRef.current) {
      return domOrderCacheRef.current;
    }

    const uuids = Object.keys(formFields.current);
    const form = formRef.current;

    if (form) {
      const domIds = Array.from(form.querySelectorAll("[id]")).map(
        (el) => el.id,
      );
      uuids.sort((a, b) => {
        const ai = domIds.indexOf(a);
        const bi = domIds.indexOf(b);
        if (ai === -1 && bi === -1) return 0;
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
      });
    }

    domOrderCacheRef.current = uuids;
    return uuids;
  }, []);

  const getFieldSchema = useCallback((): FormFieldSchema[] => {
    return getDomOrderedUuids()
      .filter((uuid) => uuid in formFields.current)
      .map((uuid) => {
        const { name, getValue, metadata } = formFields.current[uuid];
        return {
          name: name ?? uuid,
          value: getValue ? getValue() : undefined,
          errors: errorsRef.current[uuid] ?? [],
          ...metadata,
        };
      });
  }, [getDomOrderedUuids]);

  const onValuesChangeRef = useRef(onValuesChange);
  onValuesChangeRef.current = onValuesChange;
  const onSchemaChangeRef = useRef(onSchemaChange);
  onSchemaChangeRef.current = onSchemaChange;

  const schemaChangePendingRef = useRef(false);

  const fireSchemaChange = useCallback(() => {
    if (schemaChangePendingRef.current) return;
    schemaChangePendingRef.current = true;
    Promise.resolve().then(() => {
      schemaChangePendingRef.current = false;
      setSchemaVersion((v) => v + 1);
      if (onSchemaChangeRef.current) {
        onSchemaChangeRef.current(getFieldSchema());
      }
    });
  }, [getFieldSchema]);

  const notifyValueChanged = useCallback(() => {
    if (trackDirty) {
      checkDirty();
    }
    if (onValuesChangeRef.current) {
      onValuesChangeRef.current(getFormData());
    }
    fireSchemaChange();
  }, [trackDirty, checkDirty, getFormData, fireSchemaChange]);

  /**
   * Re-snapshots all current field values as the new baseline.
   */
  const resetDirty = useCallback(() => {
    log("lightBlue", "Form", "resetDirty");
    const snapshot: Record<string, unknown> = {};
    for (const [uuid, { getValue }] of Object.entries(formFields.current)) {
      if (getValue) {
        snapshot[uuid] = getValue();
      }
    }
    initialFieldValuesRef.current = snapshot;
    setIsDirty(false);
  }, [log]);

  /**
   * Validates form fields. When ids are provided, validates only those fields.
   * When ids are omitted, validates all registered fields.
   */
  const validateFields: FormContextState["validateFields"] = useCallback(
    (ids) => {
      let valid = true;
      const errors: Record<string, TranslationKey[]> = {};
      const data: FormData = {};
      let firstErrorFieldID: string | null = null;

      const fieldsToValidate = ids
        ? Object.entries(formFields.current).filter(
            ([uuid, { name }]) =>
              ids.includes(uuid) || (!!name && ids.includes(name)),
          )
        : Object.entries(formFields.current);

      fieldsToValidate.forEach(([uuid, { validate }]) => {
        const { success, errors: fieldErrors, value, name } = validate();

        // Collect value using name if available, fallback to uuid
        const key = name ?? uuid;
        data[key] = value;

        if (!success) {
          if (valid) {
            valid = false;

            if (firstErrorFieldID === null) {
              firstErrorFieldID = uuid;
            }
          }
          errors[uuid] = (fieldErrors as TranslationKey[]) ?? [];
        }
      });

      if (firstErrorFieldID) {
        pubSub?.publish("inputFocus", { id: firstErrorFieldID });
      }

      // When validating all fields (no ids), replace errors; otherwise merge
      if (ids) {
        setErrors((prev) => ({ ...prev, ...errors }));
      } else {
        setErrors(errors);
      }

      // Bump schemaVersion so consumers (e.g. FormFlowTemplate) re-read
      // the schema with updated error arrays.
      fireSchemaChange();

      return { valid, errors, data };
    },
    [pubSub, fireSchemaChange],
  );

  const handleSubmit = (e: FormEvent) => {
    const { valid, errors, data } = validateFields();
    log("lightBlue", ["Form", "handleSubmit"], valid ? "✅" : "❌", errors);

    e.preventDefault();
    if (formDisabled || !valid) {
      return;
    }
    resetDirty();
    onSubmit?.(e, valid, errors, data);
  };

  const registerValidatable: FormContextState["registerValidatable"] =
    useCallback(
      (uuid, validate, name, getValue, metadata) => {
        formFields.current[uuid] = { validate, name, getValue, metadata };
        domOrderCacheRef.current = null;
        if (
          trackDirty &&
          getValue &&
          !(uuid in initialFieldValuesRef.current)
        ) {
          const initialValue = getValue();
          initialFieldValuesRef.current[uuid] = initialValue;
          log(
            "lightBlue",
            "Form",
            `registerValidatable: ${name ?? uuid} (initial: ${JSON.stringify(initialValue)})`,
          );
        }
        fireSchemaChange();
      },
      [trackDirty, log, fireSchemaChange],
    );

  const deregisterValidatable = useCallback(
    (uuid: string) => {
      delete formFields.current[uuid];
      delete initialFieldValuesRef.current[uuid];
      domOrderCacheRef.current = null;
      if (trackDirty) {
        checkDirty();
      }
      fireSchemaChange();
    },
    [trackDirty, checkDirty, fireSchemaChange],
  );

  const resetValidatable = useCallback((uuid: string) => {
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[uuid];
      return newErrors;
    });
  }, []);

  const contextValue = useMemo<FormContextState>(
    () => ({
      disabled: formDisabled,
      registerValidatable,
      deregisterValidatable,
      resetValidatable,
      validateFields,
      errors,
      disable,
      enable,
      isDirty,
      resetDirty,
      notifyValueChanged,
      getFormData,
      getFieldSchema,
      schemaVersion,
    }),
    [
      formDisabled,
      registerValidatable,
      deregisterValidatable,
      resetValidatable,
      validateFields,
      errors,
      isDirty,
      resetDirty,
      notifyValueChanged,
      getFormData,
      getFieldSchema,
      schemaVersion,
      disable,
      enable,
    ],
  );

  return (
    <FormContext.Provider value={contextValue}>
      <Flex
        ref={mergeRefs(ref, formRef)}
        as="form"
        className={formStyle}
        direction="column"
        gap={gap}
        action=""
        onSubmit={handleSubmit}
        {...props}
      >
        {trackDirty && <FormNavigationGuard />}
        {children}
      </Flex>
    </FormContext.Provider>
  );
};
